import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Paper, Stack, Chip, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Alert, Divider,
} from '@mui/material';
import { api } from '../api/client';
import type { AccessEventDto, PageResponse, VehicleDto } from '../api/types';

export function ManualReviewPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<AccessEventDto | null>(null);
  const [vehicleId, setVehicleId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const events = useQuery({
    queryKey: ['manual-review'],
    queryFn: () => api.get<AccessEventDto[]>('/access-events/manual-review').then((r) => r.data),
    refetchInterval: 30_000,
  });

  const vehicles = useQuery({
    queryKey: ['vehicles-all-active'],
    queryFn: () => api.get<PageResponse<VehicleDto>>('/vehicles', { params: { size: 500, status: 'ACTIVE' } }).then((r) => r.data.content),
  });

  const submit = useMutation({
    mutationFn: () => {
      if (!open || vehicleId === '') return Promise.reject(new Error('falta seleccionar vehículo'));
      return api.post(`/access-events/${open.id}/override`, { vehicleId, reason });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-review'] });
      setOpen(null);
      setVehicleId('');
      setReason('');
      setErr(null);
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      setErr(ax.response?.data?.message ?? 'Error');
    },
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Revisión manual</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Eventos donde la IA no pudo identificar la placa o no encontró match en catálogo.
      </Typography>

      <Stack spacing={2}>
        {events.data?.length === 0 && <Alert severity="success">Sin pendientes 🎉</Alert>}
        {(events.data ?? []).map((e) => (
          <Paper key={e.id} sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {e.imagePresignedUrl && (
                <img src={e.imagePresignedUrl} alt="placa" style={{ width: 220, height: 165, objectFit: 'cover', borderRadius: 4 }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip label={e.errorCode ?? 'UNRESOLVED'} color="warning" size="small" />
                  <Chip label={e.status} size="small" />
                  <Typography variant="caption">{new Date(e.capturedAt).toLocaleString()}</Typography>
                </Stack>
                <Typography>Placa leída: <b>{e.detectedPlate ?? '(no legible)'}</b></Typography>
                <Typography variant="body2">Confianza: {e.confidence ?? '—'}</Typography>
                {e.errorMessage && <Typography variant="caption" color="text.secondary">{e.errorMessage}</Typography>}
              </Box>
              <Button variant="contained" onClick={() => setOpen(e)}>Asignar manualmente</Button>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override manual</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
            <TextField select label="Vehículo correcto" value={vehicleId} onChange={(e) => setVehicleId(e.target.value === '' ? '' : Number(e.target.value))} fullWidth>
              {(vehicles.data ?? []).map((v) => (
                <MenuItem key={v.id} value={v.id}>{v.plateDisplay} — {v.driver?.fullName ?? '(sin chofer)'}</MenuItem>
              ))}
            </TextField>
            <TextField label="Razón (mín 10 chars)" value={reason} onChange={(e) => setReason(e.target.value)} multiline rows={3} fullWidth required />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Esto registrará una entrada o salida (lo que corresponda) y guardará auditoría.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)}>Cancelar</Button>
          <Button variant="contained" disabled={vehicleId === '' || reason.length < 10 || submit.isPending} onClick={() => submit.mutate()}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
